import { ThemeEvents } from '@theme/events';

/**
 * Barre d'ajout au panier fixe affichée lorsque le bouton principal a été dépassé au scroll.
 */
class AddToCartSticky extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {MutationObserver | undefined} */
  #syncObserver;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    const sourceButton = this.#getSourceButton();
    const stickyButton = this.querySelector('[data-sticky-add-button]');

    if (!(sourceButton instanceof HTMLButtonElement)) return;
    if (!(stickyButton instanceof HTMLButtonElement)) return;

    const formId = this.dataset.form;

    if (formId) {
      stickyButton.setAttribute('form', formId);
    }

    stickyButton.addEventListener('click', (event) => {
      event.preventDefault();
      sourceButton.click();
    });

    this.#syncButton(sourceButton, stickyButton);

    this.#syncObserver = new MutationObserver(() => {
      this.#syncButton(sourceButton, stickyButton);
    });

    this.#syncObserver.observe(sourceButton, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['disabled', 'class', 'aria-disabled'],
    });

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        this.#setStickyVisible(this.#shouldShowSticky(entry));
      },
      { threshold: 0, rootMargin: '0px 0px -1px 0px' }
    );

    this.#intersectionObserver.observe(sourceButton);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#syncObserver?.disconnect();
    this.#abortController?.abort();
  }

  /**
   * Affiche le sticky uniquement après avoir scrollé au-delà du bouton source
   * (bouton au-dessus du viewport), pas lorsqu'il est encore en dessous.
   *
   * @param {IntersectionObserverEntry} entry
   * @returns {boolean}
   */
  #shouldShowSticky(entry) {
    if (entry.isIntersecting) return false;

    return entry.boundingClientRect.top < 0;
  }

  /**
   * @param {boolean} showSticky
   */
  #setStickyVisible(showSticky) {
    this.classList.toggle('is-visible', showSticky);
    this.toggleAttribute('inert', !showSticky);
    this.setAttribute('aria-hidden', showSticky ? 'false' : 'true');
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const productId = this.dataset.productId;

    if (!productId) return;

    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail?.data?.productId && event.detail.data.productId !== productId) {
      return;
    }

    const resource = event.detail?.resource;

    if (resource) {
      this.#updateProductImage(resource);
    }
  };

  /**
   * @param {Object} resource
   * @param {Object} [resource.featured_media]
   * @param {Object} [resource.featured_media.preview_image]
   * @param {string} [resource.featured_media.preview_image.src]
   * @param {string} [resource.featured_media.preview_image.alt]
   */
  #updateProductImage(resource) {
    const img = this.querySelector('[data-sticky-product-image]');
    const mediaWrapper = this.querySelector('.c-atc-sticky__media');

    if (!(img instanceof HTMLImageElement)) return;

    const preview = resource?.featured_media?.preview_image;

    if (!preview?.src) {
      const defaultImage = this.dataset.defaultImage;
      if (defaultImage) {
        img.src = defaultImage;
        img.hidden = false;
        if (mediaWrapper instanceof HTMLElement) mediaWrapper.hidden = false;
      } else {
        img.hidden = true;
        if (mediaWrapper instanceof HTMLElement) mediaWrapper.hidden = true;
      }
      return;
    }

    img.hidden = false;

    if (mediaWrapper instanceof HTMLElement) {
      mediaWrapper.hidden = false;
    }

    try {
      const url = new URL(preview.src, window.location.origin);
      url.searchParams.set('width', '160');
      img.src = url.toString();
    } catch {
      img.src = preview.src;
    }

    if (preview.alt) {
      img.alt = preview.alt;
    }
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getSourceButton() {
    const sourceId = this.dataset.source;

    if (sourceId) {
      const byId = document.getElementById(sourceId);

      if (byId instanceof HTMLButtonElement) return byId;
    }

    const root = this.closest('.c-buy-btns');

    if (!root) return null;

    const button = root.querySelector('button[name="add"]');

    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @param {HTMLButtonElement} sourceButton
   * @param {HTMLButtonElement} stickyButton
   */
  #syncButton(sourceButton, stickyButton) {
    stickyButton.disabled = sourceButton.disabled;

    if (sourceButton.disabled) {
      stickyButton.setAttribute('disabled', '');
    } else {
      stickyButton.removeAttribute('disabled');
    }

    const sourceContent = sourceButton.querySelector('.add-to-cart-text__content');
    const stickyContent = stickyButton.querySelector('.add-to-cart-text__content');

    if (sourceContent && stickyContent) {
      stickyContent.textContent = sourceContent.textContent;
    }

    const sourceAdded = sourceButton.querySelector('.add-to-cart-text--added');
    const stickyAdded = stickyButton.querySelector('.add-to-cart-text--added');

    if (sourceAdded && stickyAdded) {
      stickyAdded.className = sourceAdded.className;
    }

    const sourceText = sourceButton.querySelector('.add-to-cart-text');
    const stickyText = stickyButton.querySelector('.add-to-cart-text');

    if (sourceText && stickyText) {
      stickyText.className = sourceText.className;
    }
  }
}

if (!customElements.get('add-to-cart-sticky')) {
  customElements.define('add-to-cart-sticky', AddToCartSticky);
}
